import { openDB, type IDBPDatabase } from "idb";
import { DB_NAME, DB_VERSION } from "../config.ts";

/**
 * The local store mirrors the server tables one-for-one, keyed by the same
 * uuid. Ids are generated on the device, so a row has one identity from the
 * moment it is created — there is no local id to remap after a sync, and a
 * replayed write lands on the row it already wrote.
 */
export const STORES = [
  "sauna", "stove", "position", "stove_event",
  "heat_session", "measurement", "reading", "bathing_group", "fuel_load",
  "rating", "comment", "membership", "app_user",
] as const;
export type StoreName = (typeof STORES)[number];

let dbp: Promise<IDBPDatabase> | null = null;

export function db(): Promise<IDBPDatabase> {
  if (!dbp) {
    dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(d, _old, _new, tx) {
        for (const s of STORES) {
          if (s === "membership") continue;          // composite key, below
          if (!d.objectStoreNames.contains(s)) d.createObjectStore(s, { keyPath: "id" });
        }
        /* membership is keyed on (sauna_id, user_id) in the database — it has
           no id column at all. Storing it under keyPath "id" made every pull
           throw as soon as a membership row came down. */
        if (d.objectStoreNames.contains("membership")) {
          const existing = tx.objectStore("membership" as never);
          if (!Array.isArray(existing.keyPath)) d.deleteObjectStore("membership");
        }
        if (!d.objectStoreNames.contains("membership")) {
          d.createObjectStore("membership", { keyPath: ["sauna_id", "user_id"] });
        }
        if (!d.objectStoreNames.contains("outbox")) {
          d.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
        }
        if (!d.objectStoreNames.contains("meta")) {
          d.createObjectStore("meta");
        }
        // Writes the server refused for good. Kept, never silently dropped.
        if (!d.objectStoreNames.contains("rejected")) {
          d.createObjectStore("rejected", { keyPath: "seq" });
        }
      },
    });
  }
  return dbp;
}

/** Test seam: forget the handle so a fresh fake-indexeddb can be opened. */
export function _resetDb() { dbp = null; }

/**
 * Awaited, not fired and forgotten. It used to drop the put's promise on the
 * floor, so a rejected write escaped every try/catch around it and surfaced
 * as an unhandled rejection in the console — the sync reported success while
 * nothing had been stored.
 */
export async function put<T extends object>(store: StoreName, row: T): Promise<void> {
  await (await db()).put(store, row as never);
}
/**
 * Read-modify-write inside one transaction.
 *
 * Doing it as a separate get and put loses updates whenever two of them are
 * in flight at once: both read the same row, both write their own field onto
 * it, and the second erases the first. Three patches fired together — an
 * outdoor temperature, a wind direction, a source — left only the last one.
 * An IndexedDB transaction is atomic, which is the whole fix.
 */
export async function mutate<T extends object>(
  store: StoreName, id: string, change: (row: T | undefined) => T | undefined,
): Promise<T | undefined> {
  const d = await db();
  const tx = d.transaction(store, "readwrite");
  const current = (await tx.store.get(id)) as T | undefined;
  const next = change(current);
  if (next !== undefined) await tx.store.put(next as never);
  await tx.done;
  return next;
}

export async function getAll<T>(store: StoreName): Promise<T[]> {
  return (await db()).getAll(store) as Promise<T[]>;
}
export async function get<T>(store: StoreName, id: string): Promise<T | undefined> {
  return (await db()).get(store, id) as Promise<T | undefined>;
}
export async function meta<T>(key: string): Promise<T | undefined> {
  return (await db()).get("meta", key) as Promise<T | undefined>;
}
export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put("meta", value, key);
}
